/**
 * Browse Sites Page Loader
 * Fetches marketplace sites from the API before rendering.
 */

import type { LoaderFunctionArgs } from 'react-router-dom';
import { requireAuth, getSearchParams } from './helpers';
import {
  trackRouteLoad,
  trackRouteError,
  startRouteTimer,
  calculateDataSize,
} from '@/utils/route-analytics';
import { fetchMarketplaceSites, type MarketplaceResponse } from '@/services/marketplaceService';
import { transformApiSite } from '@/components/browse-sites/helpers';
import type { Site, PaginationState } from '@/components/browse-sites/types';

export interface BrowseSitesData {
  sites: Site[];
  pagination: PaginationState;
}

/**
 * Browse Sites loader function
 */
export async function browseSitesLoader(args: LoaderFunctionArgs): Promise<BrowseSitesData> {
  const getElapsed = startRouteTimer();

  try {
    await requireAuth(args.request);

    const searchParams = getSearchParams(args);

    // Build API params from URL search params
    const apiParams = {
      page: Number(searchParams.get('page')) || 1,
      per_page: Number(searchParams.get('per_page')) || 25,
      search: searchParams.get('search') ?? undefined,
      category: searchParams.get('category') ?? undefined,
      da_min: searchParams.get('da_min') ? Number(searchParams.get('da_min')) : undefined,
      da_max: searchParams.get('da_max') ? Number(searchParams.get('da_max')) : undefined,
      sort_by: searchParams.get('sort_by') ?? 'domain_authority',
      sort_order: (searchParams.get('sort_order') as 'asc' | 'desc') || 'desc',
    };

    let response: MarketplaceResponse;
    try {
      response = await fetchMarketplaceSites(apiParams);
    } catch {
      // Return empty set on API failure so the page still renders
      response = {
        sites: [],
        pagination: {
          current_page: 1,
          per_page: 25,
          total: 0,
          last_page: 1,
        },
      };
    }

    const sites = response.sites.map(transformApiSite);

    const data: BrowseSitesData = {
      sites,
      pagination: response.pagination,
    };

    const loadTime = getElapsed();
    const dataSize = calculateDataSize(data);
    trackRouteLoad('/browse-sites', loadTime, dataSize);

    return data;
  } catch (error) {
    if (error instanceof Response) {
      throw error;
    }
    trackRouteError('/browse-sites', error as Error);
    throw error;
  }
}
